import { setContext } from '@apollo/client/link/context';
import { merge, Observable } from 'rxjs';
import { ofType } from 'src/core/engine/rxjs/ofType';
import { STORAGE_KEY, UserActions } from 'src/core/state/user';
import { ignoreElements, map, tap, withLatestFrom } from 'rxjs';
import { Actions as RouterActions, LOCATION_CHANGE } from 'src/core/engine/router/router.register';
import { fireGtm } from 'src/features/analytics/hooks/useGtm';
import { getBrowserCookies, isIrishUserCookieValue } from 'src/util/cookies';

import { externalLink } from './epics/externalLink.epic';
import { appReducer } from './app.reducer';
import { alertEpic } from './epics/alert.epic';

const readIrishUserCookie = () => isIrishUserCookieValue(getBrowserCookies().get('isIrishUser', { doNotParse: true }));

export function appRegister() {
    let token = null;
    let isIrishUser = readIrishUserCookie();

    // This code intercepts all REST API calls made via fetch and XMLHttpRequest, rewriting URLs that contain '/rest/V1/' to '/rest/{store-id}/V1/'.
    const originalFetch = window.fetch;

    window.fetch = async (...args) => {
        let url = args[0];
        const options = args[1];

        if (typeof url === 'string' && url.includes('/rest/V1/') && isIrishUser) {
            url = url.replace('/rest/V1/', '/rest/ireland/V1/');
        }

        return originalFetch(url, options);
    };

    const originalXHR = window.XMLHttpRequest;
    class InterceptedXHR extends originalXHR {
        open(method: string, url: string) {
            if (url.includes('/rest/V1/') && isIrishUser) {
                url = url.replace('/rest/V1/', '/rest/ireland/V1/');
            }
            super.open(method, url);
        }
    }

    window.XMLHttpRequest = InterceptedXHR;

    const link = setContext((request, previousContext) => {
        const next = {
            ...previousContext.headers,
        };

        isIrishUser = readIrishUserCookie();

        /**
         * This is to allow none-visible products in queries
         */
        switch (request.operationName) {
            case 'productVariant': {
                next['Allow-NonVisible'] = 'true';
                break;
            }
        }

        /**
         * This is to allow authenticated gql requests
         */

        if (token !== null && !request.variables?.noAuthorization) {
            next['Authorization'] = `Bearer ${token}`;
        }

        next['store'] = isIrishUser ? 'ireland' : 'default';

        /**
         * This is what the setContext link expects
         */
        return {
            headers: next,
        };
    });

    return {
        epics: [
            alertEpic,
            externalLink,
            (actions: Observable<any>, state$: Observable<any>, deps) => {
                const initial = deps.storage.get(STORAGE_KEY);

                if (initial && initial.token) {
                    token = initial.token;
                }
                const token$ = state$.pipe(map((s) => s?.user?.token));
                const signIn$ = actions.pipe(
                    ofType<UserActions>('User.SignInSuccess', 'User.Persist'),
                    withLatestFrom(token$),
                    tap(([, _token]) => (token = _token)),
                );
                const signOut$ = actions.pipe(
                    ofType<UserActions>('User.SignOut', 'User.Reset'),
                    tap(() => (token = null)),
                );
                return merge(signIn$, signOut$).pipe(ignoreElements());
            },
            (actions$: Observable<any>) => {
                return actions$.pipe(
                    ofType<RouterActions>(LOCATION_CHANGE),
                    tap(() => fireGtm({ event: 'pageview' })),
                    ignoreElements(),
                );
            },
        ],
        reducers: {
            app: appReducer,
        },
        name: 'app',
        links: [link],
    };
}
